import NextLink from 'next/link';
import PropTypes from 'prop-types';
import { memo } from 'react';
import { twMerge } from 'tailwind-merge';

import Heading from 'shopper/components/Heading';
import Icon from 'shopper/components/Icon';
import Tag from 'shopper/components/Tag/V1';
import Text from 'shopper/components/Text';

import Anchor from 'components/Anchor';
import UserAvatar from 'components/UserAvatar';
import UserGroup from 'components/UserGroup';

import { formatDateByDayFullMonthYearHourMinute, getTimeTo } from 'lib/date';
import { toThreadLink } from 'lib/links';
import placeholder from 'lib/placeholder';
import { noop } from 'lib/utils';

const ThreadsListItem = ({
  className,
  isFixed = false,
  threadCategoryName,
  threadComments,
  threadLikes,
  threadPublished,
  threadSlug,
  threadTitle,
  threadUpdated,
  threadUsers = [],
  threadViews,
  userId,
  userLevel = null,
  userName,
  userPhoto,
  userTypeName = null,
  onClick = noop,
}) => (
  <NextLink href={toThreadLink(threadSlug)} prefetch={false} passHref>
    <Anchor
      className={twMerge(
        'relative flex w-full flex-row border-b border-solid border-neutral-high-200 p-4 dark:border-neutral-low-300 md:p-6',
        isFixed && 'bg-communication-100 dark:bg-communication-500',
        className
      )}
      onClick={onClick}
    >
      <div className="flex w-full">
        <UserAvatar
          userId={userId}
          userLevel={userLevel}
          userName={userName}
          userPhoto={userPhoto}
          userTypeName={userTypeName}
        />
        <div className="relative ml-4 flex w-full flex-col md:flex-row">
          <div className="relative mb-2 flex w-full flex-row items-center md:mb-0 md:items-start md:pr-6">
            <div className="w-full">
              <Heading as="h2" className="w-full" size="size5">
                {threadTitle}
              </Heading>
              <div className="mt-1 flex flex-col items-start md:mt-2 md:flex-row md:items-center">
                <Text className="mb-2 md:mb-0" size="size2">
                  Por <b>{userName}</b>
                </Text>
                <Text className="mx-2 hidden md:inline">•</Text>
                <Text size="size2">
                  {formatDateByDayFullMonthYearHourMinute(threadPublished)}
                </Text>
              </div>
              <div className="mt-2 flex w-full flex-col justify-start md:flex-row md:justify-between">
                <div className="flex">
                  {isFixed && (
                    <Tag className="mr-1" type="primary" filled>
                      {placeholder('descriptions.fixedThread')}
                    </Tag>
                  )}
                  {threadCategoryName && <Tag filled>{threadCategoryName}</Tag>}
                </div>
                <div className="mt-3 flex items-center text-neutral-low-500 dark:text-neutral-high-100 md:mt-0">
                  <div className="mr-2 flex items-center">
                    <Icon className="mr-1" name="visibility" size="size3" />
                    <Text size="size2">{threadViews}</Text>
                  </div>
                  •
                  <div className="mx-2 flex items-center">
                    <Icon className="mr-1" name="like-filled" size="size3" />
                    <Text size="size2">{threadLikes}</Text>
                  </div>
                  •
                  <div className="ml-2 flex items-center">
                    <Icon className="mr-1" name="chat-filled" size="size3" />
                    <Text size="size2">{threadComments}</Text>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex min-w-[268px] items-center border-t border-neutral-high-200 pt-2 dark:border-neutral-low-300 md:border-l md:border-t-0 md:py-2 md:pl-6">
            {threadComments > 0 ? (
              <div>
                <Text className="inline">
                  <b>Última atualização:</b> há
                  {` ${getTimeTo(threadUpdated, { shortened: true })}`}
                </Text>
                <UserGroup
                  className="mt-2 hidden md:flex"
                  users={threadUsers}
                />
              </div>
            ) : (
              <div>
                <Text
                  className="whitespace-nowrap"
                  size="size2"
                >{`Esse tópico ainda não tem respostas \n`}</Text>
                <Text size="size2" bold>
                  {placeholder('descriptions.beFirstToComment')}
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>
    </Anchor>
  </NextLink>
);

ThreadsListItem.propTypes = {
  className: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  isFixed: PropTypes.bool,
  userName: PropTypes.string.isRequired,
  userPhoto: PropTypes.string.isRequired,
  userLevel: PropTypes.number,
  userId: PropTypes.number.isRequired,
  userTypeName: PropTypes.string,
  threadViews: PropTypes.number.isRequired,
  threadUsers: PropTypes.arrayOf(
    PropTypes.shape({
      userId: PropTypes.number.isRequired,
      userLevel: PropTypes.number,
      userName: PropTypes.string.isRequired,
      userPhoto: PropTypes.string.isRequired,
      userUsername: PropTypes.string.isRequired,
    })
  ),
  threadCategoryName: PropTypes.string.isRequired,
  threadComments: PropTypes.number.isRequired,
  threadLikes: PropTypes.number.isRequired,
  threadPublished: PropTypes.string.isRequired,
  threadSlug: PropTypes.string.isRequired,
  threadTitle: PropTypes.string.isRequired,
  threadUpdated: PropTypes.string.isRequired,
  onClick: PropTypes.func,
};

export default memo(ThreadsListItem);
